import { PIECE_TYPE_IDS } from "./pieceTypeIds";

const LEVEL_DATA_SCRIPT_GUID = "4b041755549c5d54db8583455ad22e5a";
const DEFAULT_LEVEL_FILE_ID = 11400000;
const DEFAULT_LEVEL_DIFFICULTY = "Normal";
const DEFAULT_PIECE_PREFABS = [
  { fileID: "8253798089275986044", guid: "49260aee09f484046b5c4dc30a67ce9c", type: 3 },
  { fileID: "8253798089275986044", guid: "a575438563c430f4ba6a7f129efb9553", type: 3 },
  { fileID: "8253798089275986044", guid: "7ee90ab0865fbc84886637d0d4de5157", type: 3 },
  { fileID: "4956334306523661366", guid: "f9a217efbd63bdc48a8b2a662115d1a3", type: 3 }
];
const GAMEPLAY_TYPE_TO_ID = {
  ThreatClear: 0,
  MalikFight: 1
};
const LEVEL_DIFFICULTY_TO_ID = {
  Normal: 0,
  Hard: 1,
  VeryHard: 2
};

function hasPieceType(type) {
  return Object.prototype.hasOwnProperty.call(PIECE_TYPE_IDS, type);
}

function toInt(value, fallback = 0) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toFloat(value, fallback = 0) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toBoolInt(value, fallback = false) {
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") return value !== 0 ? 1 : 0;
  if (typeof value === "string") {
    if (value === "true") return 1;
    if (value === "false") return 0;
  }
  return fallback ? 1 : 0;
}

function toPieceTypeId(rawType, fallback = "Empty") {
  const candidates = [];

  if (typeof rawType === "number" && Number.isFinite(rawType)) {
    return toInt(rawType, 0);
  }

  const value = typeof rawType === "string" ? rawType.trim() : "";
  if (value) {
    if (/^-?\d+$/.test(value)) {
      return toInt(value, 0);
    }
    candidates.push(value);
    if (value.includes("_")) {
      const base = value.replace(/_\d+$/, "");
      if (base && base !== value) candidates.push(base);
    } else {
      candidates.push(`${value}_0`);
    }
  }

  if (fallback) candidates.push(fallback);

  for (const candidate of candidates) {
    if (hasPieceType(candidate)) return PIECE_TYPE_IDS[candidate];
  }

  return PIECE_TYPE_IDS.Empty;
}

function sanitizeVector2(raw, fallbackX = -1, fallbackY = -1) {
  if (!raw || typeof raw !== "object") {
    return { x: fallbackX, y: fallbackY };
  }
  return {
    x: toInt(raw.x, fallbackX),
    y: toInt(raw.y, fallbackY)
  };
}

function encodeInt32LEHex(value) {
  const intValue = toInt(value, 0) >>> 0;
  const byte0 = (intValue & 0xff).toString(16).padStart(2, "0");
  const byte1 = ((intValue >>> 8) & 0xff).toString(16).padStart(2, "0");
  const byte2 = ((intValue >>> 16) & 0xff).toString(16).padStart(2, "0");
  const byte3 = ((intValue >>> 24) & 0xff).toString(16).padStart(2, "0");
  return `${byte0}${byte1}${byte2}${byte3}`;
}

function encodePieceTypeListHex(values) {
  if (!Array.isArray(values) || values.length === 0) return "";
  return values.map((value) => encodeInt32LEHex(toPieceTypeId(value))).join("");
}

function encodeBasicPiecesHex(values) {
  if (!Array.isArray(values) || values.length === 0) return "";
  return values.map((value) => encodeInt32LEHex(toPieceTypeId(value, "Piece_Red"))).join("");
}

function yamlString(value) {
  const text = `${value ?? ""}`;
  if (text === "") return "";
  if (/^[A-Za-z0-9_.-]+$/.test(text)) return text;
  return `'${text.replace(/'/g, "''")}'`;
}

function pushIntListField(lines, fieldIndent, fieldName, values) {
  if (!Array.isArray(values) || values.length === 0) {
    lines.push(`${fieldIndent}${fieldName}: `);
    return;
  }
  lines.push(`${fieldIndent}${fieldName}:`);
  values.forEach((value) => {
    lines.push(`${fieldIndent}  - ${toInt(value, 0)}`);
  });
}

function serializePiece(piece, index, board, indent) {
  const width = Math.max(1, toInt(board?.collumn, 9));
  const fallbackCollumn = index % width;
  const fallbackLine = Math.floor(index / width);
  const collumn = toInt(piece?.collumn, fallbackCollumn);
  const line = toInt(piece?.line, fallbackLine);
  const parent = sanitizeVector2(piece?.parent, line, collumn);
  const linkedTeleporter = sanitizeVector2(piece?.linkedTeleporter, -1, -1);
  const linkedHead = sanitizeVector2(piece?.linkedHead, -1, -1);
  const linkedFront = sanitizeVector2(piece?.linkedFront, -1, -1);
  const spawnerData = piece?.spawnerData ?? {};
  const spawnablePieces = encodePieceTypeListHex(spawnerData.spawnablePieces);
  const sequentialPieces = encodePieceTypeListHex(spawnerData.sequentialPieces);

  const lines = [];
  lines.push(`${indent}- collumn: ${collumn}`);
  lines.push(`${indent}  line: ${line}`);
  lines.push(`${indent}  type: ${toPieceTypeId(piece?.type)}`);
  lines.push(`${indent}  backgroundType: ${toPieceTypeId(piece?.backgroundType)}`);
  lines.push(`${indent}  foregroundType: ${toPieceTypeId(piece?.foregroundType)}`);
  lines.push(`${indent}  parent: {x: ${parent.x}, y: ${parent.y}}`);
  lines.push(`${indent}  spawnerData:`);
  lines.push(`${indent}    spawnablePieces: ${spawnablePieces}`);
  lines.push(`${indent}    sequentialPieces: ${sequentialPieces}`);
  lines.push(`${indent}    quantity: ${Math.max(0, toInt(spawnerData.quantity, 0))}`);
  lines.push(`${indent}  pieceColor: ${toPieceTypeId(piece?.pieceColor)}`);
  lines.push(`${indent}  startImune: ${toBoolInt(piece?.startImune, false)}`);
  lines.push(`${indent}  isReceiver: ${toBoolInt(piece?.isReceiver, false)}`);
  lines.push(
    `${indent}  linkedTeleporter: {x: ${linkedTeleporter.x}, y: ${linkedTeleporter.y}}`
  );
  lines.push(`${indent}  stretcherLenght: ${Math.max(1, toInt(piece?.stretcherLenght, 1))}`);
  lines.push(`${indent}  stretcherDirection: ${toInt(piece?.stretcherDirection, 2)}`);
  lines.push(`${indent}  linkedHead: {x: ${linkedHead.x}, y: ${linkedHead.y}}`);
  lines.push(`${indent}  linkedFront: {x: ${linkedFront.x}, y: ${linkedFront.y}}`);
  lines.push(`${indent}  blobSubstitute: ${toPieceTypeId(piece?.blobSubstitute, "RandomElement")}`);
  lines.push(
    `${indent}  overrideMalikDeathSpawnRows: ${toBoolInt(piece?.overrideMalikDeathSpawnRows, false)}`
  );
  pushIntListField(lines, `${indent}  `, "malikDeathSpawnRowsOverride", piece?.malikDeathSpawnRowsOverride);
  lines.push(
    `${indent}  overrideMalikDeathSpawnColumns: ${toBoolInt(
      piece?.overrideMalikDeathSpawnColumns,
      false
    )}`
  );
  pushIntListField(
    lines,
    `${indent}  `,
    "malikDeathSpawnColumnsOverride",
    piece?.malikDeathSpawnColumnsOverride
  );
  lines.push(
    `${indent}  overrideMalikThreatLife: ${toBoolInt(piece?.overrideMalikThreatLife, false)}`
  );
  lines.push(
    `${indent}  malikThreatLifeOverride: ${Math.max(1, toInt(piece?.malikThreatLifeOverride, 1))}`
  );
  return lines;
}

function pushBoardField(lines, fieldIndent, fieldName, board) {
  const collumn = Math.max(1, toInt(board?.collumn, 9));
  const line = Math.max(1, toInt(board?.line, 9));
  const pieces = Array.isArray(board?.pieces) ? board.pieces : [];

  lines.push(`${fieldIndent}${fieldName}:`);
  lines.push(`${fieldIndent}  collumn: ${collumn}`);
  lines.push(`${fieldIndent}  line: ${line}`);
  lines.push(`${fieldIndent}  pieces:`);
  pieces.forEach((piece, index) => {
    lines.push(...serializePiece(piece, index, { collumn, line }, `${fieldIndent}  `));
  });
}

function pushBoardList(lines, fieldIndent, boards) {
  if (!Array.isArray(boards) || boards.length === 0) {
    lines.push(`${fieldIndent}malikBoardPhases: []`);
    return;
  }

  lines.push(`${fieldIndent}malikBoardPhases:`);
  boards.forEach((board) => {
    const collumn = Math.max(1, toInt(board?.collumn, 9));
    const line = Math.max(1, toInt(board?.line, 9));
    const pieces = Array.isArray(board?.pieces) ? board.pieces : [];

    lines.push(`${fieldIndent}- collumn: ${collumn}`);
    lines.push(`${fieldIndent}  line: ${line}`);
    lines.push(`${fieldIndent}  pieces:`);
    pieces.forEach((piece, index) => {
      lines.push(...serializePiece(piece, index, { collumn, line }, `${fieldIndent}  `));
    });
  });
}

function sanitizePrefabReference(raw) {
  if (!raw || typeof raw !== "object") return null;

  let fileID = "0";
  if (typeof raw.fileID === "string" && /^-?\d+$/.test(raw.fileID.trim())) {
    fileID = raw.fileID.trim();
  } else if (typeof raw.fileID === "number" && Number.isFinite(raw.fileID)) {
    fileID = String(Math.trunc(raw.fileID));
  } else if (typeof raw.fileID === "bigint") {
    fileID = raw.fileID.toString();
  }

  const type = toInt(raw.type, 3);
  const guid = `${raw.guid ?? ""}`.trim();
  if (!guid) return null;
  return { fileID, guid, type };
}

function resolvePrefabReferences(level) {
  const raw = Array.isArray(level?.piecesPrefabs) ? level.piecesPrefabs : [];
  const prefabs = raw.map(sanitizePrefabReference).filter(Boolean);
  if (prefabs.length > 0) return prefabs;
  return DEFAULT_PIECE_PREFABS;
}

function pushVector2ListField(lines, fieldIndent, fieldName, values) {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) {
    lines.push(`${fieldIndent}${fieldName}: []`);
    return;
  }

  lines.push(`${fieldIndent}${fieldName}:`);
  list.forEach((entry) => {
    const cell = sanitizeVector2(entry, -1, -1);
    lines.push(`${fieldIndent}  - {x: ${cell.x}, y: ${cell.y}}`);
  });
}

function pushFirstWishForcedTargetsByColor(lines, fieldIndent, values) {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) {
    lines.push(`${fieldIndent}firstWishForcedTargetsByColor: []`);
    return;
  }

  lines.push(`${fieldIndent}firstWishForcedTargetsByColor:`);
  list.forEach((entry) => {
    lines.push(`${fieldIndent}- genieColor: ${toInt(entry?.genieColor, 0)}`);
    pushVector2ListField(lines, `${fieldIndent}  `, "cells", entry?.cells);
  });
}

function pushFirstWishForcedTargets(lines, fieldIndent, values) {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) {
    lines.push(`${fieldIndent}firstWishForcedTargets: []`);
    return;
  }

  lines.push(`${fieldIndent}firstWishForcedTargets:`);
  list.forEach((entry) => {
    const cell = sanitizeVector2(entry?.cell, -1, -1);
    lines.push(`${fieldIndent}- genieId: ${yamlString(entry?.genieId ?? "")}`);
    lines.push(`${fieldIndent}  cell: {x: ${cell.x}, y: ${cell.y}}`);
  });
}

function pushForcedMoveTileOverrides(lines, fieldIndent, fieldName, values) {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) {
    lines.push(`${fieldIndent}${fieldName}: []`);
    return;
  }

  lines.push(`${fieldIndent}${fieldName}:`);
  list.forEach((entry) => {
    const cell = sanitizeVector2(entry?.cell, -1, -1);
    lines.push(`${fieldIndent}  - cell: {x: ${cell.x}, y: ${cell.y}}`);
    lines.push(`${fieldIndent}    pieceType: ${toPieceTypeId(entry?.pieceType)}`);
  });
}

function pushForcedMoveGenieOverrides(lines, fieldIndent, fieldName, values) {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) {
    lines.push(`${fieldIndent}${fieldName}: []`);
    return;
  }

  lines.push(`${fieldIndent}${fieldName}:`);
  list.forEach((entry) => {
    lines.push(`${fieldIndent}  - genieColor: ${toInt(entry?.genieColor, 0)}`);
    pushForcedMoveTileOverrides(lines, `${fieldIndent}    `, "tileOverrides", entry?.tileOverrides);
  });
}

function pushForcedMoveSequence(lines, fieldIndent, values) {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) {
    lines.push(`${fieldIndent}forcedMoveSequence: []`);
    return;
  }

  lines.push(`${fieldIndent}forcedMoveSequence:`);
  list.forEach((entry) => {
    const activeTiles = Array.isArray(entry?.activeTiles) ? entry.activeTiles : [];
    if (activeTiles.length === 0) {
      lines.push(`${fieldIndent}- activeTiles: []`);
    } else {
      lines.push(`${fieldIndent}- activeTiles:`);
      activeTiles.forEach((tile) => {
        const cell = sanitizeVector2(tile, -1, -1);
        lines.push(`${fieldIndent}  - {x: ${cell.x}, y: ${cell.y}}`);
      });
    }

    pushVector2ListField(lines, `${fieldIndent}  `, "visibleTiles", entry?.visibleTiles);
    pushForcedMoveTileOverrides(lines, `${fieldIndent}  `, "defaultOverrides", entry?.defaultOverrides);
    pushForcedMoveGenieOverrides(
      lines,
      `${fieldIndent}  `,
      "genieColorOverrides",
      entry?.genieColorOverrides
    );
  });
}

function pushGenieColorPieceSwaps(lines, fieldIndent, values) {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) {
    lines.push(`${fieldIndent}genieColorPieceSwaps: []`);
    return;
  }

  lines.push(`${fieldIndent}genieColorPieceSwaps:`);
  list.forEach((entry) => {
    lines.push(`${fieldIndent}- genieColor: ${toInt(entry?.genieColor, 0)}`);

    const swaps = Array.isArray(entry?.swaps) ? entry.swaps : [];
    if (swaps.length === 0) {
      lines.push(`${fieldIndent}  swaps: []`);
      return;
    }

    lines.push(`${fieldIndent}  swaps:`);
    swaps.forEach((swap) => {
      lines.push(`${fieldIndent}  - from: ${toPieceTypeId(swap?.from)}`);
      lines.push(`${fieldIndent}    to: ${toPieceTypeId(swap?.to)}`);
    });
  });
}

export function buildUnityAssetText(level) {
  const levelName = yamlString(level?.id || "level_export");
  const maxMove = Math.max(1, toInt(level?.maxMove, 30));
  const initialWishProgressValue = toFloat(level?.initialWishProgressValue, 0);
  const gameplayType = GAMEPLAY_TYPE_TO_ID[level?.gameplayType] ?? 0;
  const difficulty = LEVEL_DIFFICULTY_TO_ID[level?.difficulty] ?? LEVEL_DIFFICULTY_TO_ID[DEFAULT_LEVEL_DIFFICULTY];
  const basicPieces = encodeBasicPiecesHex(level?.basicPieces);
  const prefabs = resolvePrefabReferences(level);

  const lines = [];
  lines.push("%YAML 1.1");
  lines.push("%TAG !u! tag:unity3d.com,2011:");
  lines.push(`--- !u!114 &${DEFAULT_LEVEL_FILE_ID}`);
  lines.push("MonoBehaviour:");
  lines.push("  m_ObjectHideFlags: 0");
  lines.push("  m_CorrespondingSourceObject: {fileID: 0}");
  lines.push("  m_PrefabInstance: {fileID: 0}");
  lines.push("  m_PrefabAsset: {fileID: 0}");
  lines.push("  m_GameObject: {fileID: 0}");
  lines.push("  m_Enabled: 1");
  lines.push("  m_EditorHideFlags: 0");
  lines.push(`  m_Script: {fileID: 11500000, guid: ${LEVEL_DATA_SCRIPT_GUID}, type: 3}`);
  lines.push(`  m_Name: ${levelName}`);
  lines.push("  m_EditorClassIdentifier: ");
  lines.push(`  maxMove: ${maxMove}`);
  lines.push(`  initialWishProgressValue: ${initialWishProgressValue}`);
  lines.push(`  basicPieces: ${basicPieces}`);
  lines.push(`  type: ${gameplayType}`);
  pushBoardField(lines, "  ", "board", level?.board);
  lines.push(`  randomizable: ${toBoolInt(level?.randomizable, false)}`);
  lines.push(`  difficulty: ${difficulty}`);
  lines.push(`  isTutorial: ${toBoolInt(level?.isTutorial, false)}`);
  lines.push(`  blockPowerUp: ${toBoolInt(level?.blockPowerUp, false)}`);
  lines.push(`  tutorialType: ${toInt(level?.tutorialType, 0)}`);
  lines.push(`  threatType: ${toPieceTypeId(level?.threatType)}`);
  lines.push(`  delayBeforeStartTutorial: ${toFloat(level?.delayBeforeStartTutorial, 0)}`);
  lines.push(
    `  hideChristopherOnStartLevelWindow: ${toBoolInt(
      level?.hideChristopherOnStartLevelWindow,
      false
    )}`
  );
  lines.push("  piecesPrefabs:");
  prefabs.forEach((prefab) => {
    lines.push(
      `  - {fileID: ${prefab.fileID}, guid: ${prefab.guid}, type: ${prefab.type}}`
    );
  });
  lines.push(`  skipEndGameAnimations: ${toBoolInt(level?.skipEndGameAnimations, false)}`);
  pushBoardList(lines, "  ", level?.malikBoardPhases);
  lines.push(`  malikBoardThemeId: ${yamlString(level?.malikBoardThemeId ?? "")}`);
  lines.push(`  skipMalikPlayButton: ${toBoolInt(level?.skipMalikPlayButton, false)}`);
  lines.push(`  malikSkipVsAnimation: ${toBoolInt(level?.malikSkipVsAnimation, false)}`);
  lines.push(`  lockFirstWishToTarget: ${toBoolInt(level?.lockFirstWishToTarget, false)}`);
  pushFirstWishForcedTargetsByColor(lines, "  ", level?.firstWishForcedTargetsByColor);
  pushFirstWishForcedTargets(lines, "  ", level?.firstWishForcedTargets);
  pushForcedMoveSequence(lines, "  ", level?.forcedMoveSequence);
  lines.push(`  enableGenieColorPieceSwaps: ${toBoolInt(level?.enableGenieColorPieceSwaps, false)}`);
  pushGenieColorPieceSwaps(lines, "  ", level?.genieColorPieceSwaps);
  lines.push("");
  return lines.join("\n");
}
